import { useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageGuide from "components/admin/PageGuide";
import Footer from "examples/Footer";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import SelectField from "components/admin/SelectField";
import { useConfirm } from "components/admin/Feedback";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";
import { useCan } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import { formatDate, toLocalInput } from "utils/format";

/*
 * Bố cục (người dùng: vận hành báo bảo trì, sự kiện, nhắc nhở):
 *   [Thông báo ngay]: 1 Gửi tới (toàn máy chủ / một map, khu) → 2 Kiểu, chọn bằng ô có hình minh hoạ người chơi
 *                     thấy gì → 3 Nội dung (có mẫu câu) → khung xem trước → Gửi (hỏi lại ghi rõ kiểu + nơi).
 *   [Banner tự động]: nội dung · lặp mỗi (chọn nhanh) · tới khi (chọn nhanh) · số lần sẽ chạy; danh sách còn bao lâu.
 */

// what: người chơi thấy gì; interrupt: che màn hình, bắt bấm OK.
const KIND_INFO = {
  banner: {
    label: "Chữ chạy",
    what: "Dòng chữ chạy ngang trên đầu màn hình, không làm gián đoạn. Toàn máy chủ thì còn hiện ở kênh thế giới.",
    icon: "view_day",
  },
  dialog: {
    label: "Hộp thoại",
    what: "Hộp thoại giữa màn hình, người chơi phải bấm OK mới chơi tiếp. Dùng cho tin quan trọng.",
    icon: "web_asset",
    interrupt: true,
  },
  red: {
    label: "Hộp thoại đỏ",
    what: "Như hộp thoại nhưng chữ đỏ, dùng để cảnh báo.",
    icon: "report",
    interrupt: true,
  },
  popup: {
    label: "Thông báo nhỏ",
    what: "Thông báo nhỏ trên màn hình, không bắt người chơi bấm OK.",
    icon: "notifications",
  },
  chat: {
    label: "Chat thế giới",
    what: "Một dòng chat ở kênh thế giới, đứng tên người gửi tuỳ đặt.",
    icon: "forum",
  },
};

const SERVER_KINDS = ["banner", "dialog", "popup", "chat"];
const ZONE_KINDS = ["banner", "dialog", "red", "popup"];

const TEMPLATES = [
  {
    label: "Báo bảo trì",
    text: "Máy chủ sẽ bảo trì sau 10 phút để cập nhật. Các bạn vui lòng thoát game an toàn, cảm ơn!",
  },
  {
    label: "Sự kiện bắt đầu",
    text: "Sự kiện ... đã bắt đầu! Tham gia ngay để nhận quà hấp dẫn.",
  },
  {
    label: "Đền bù",
    text: "Quà đền bù đã được gửi vào hòm thư, các bạn mở hòm thư để nhận nhé.",
  },
];

const INTERVAL_PRESETS = [5, 10, 15, 30, 60];

function endPresets() {
  const now = new Date();
  const tonight = new Date(now);
  tonight.setHours(23, 59, 0, 0);
  return [
    { label: "1 giờ nữa", value: new Date(now.getTime() + 3600000) },
    { label: "Hết hôm nay", value: tonight },
    { label: "1 ngày nữa", value: new Date(now.getTime() + 86400000) },
    { label: "7 ngày nữa", value: new Date(now.getTime() + 7 * 86400000) },
  ];
}

function timeLeft(end) {
  const ms = new Date(end).getTime() - Date.now();
  if (ms <= 0) return "đã hết";
  const minutes = Math.ceil(ms / 60000);
  if (minutes < 60) return `còn ${minutes} phút`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `còn ${hours} giờ`;
  return `còn ${Math.floor(hours / 24)} ngày`;
}

/** Hình minh hoạ đơn giản: người chơi sẽ thấy thông báo kiểu này ở đâu trên màn hình. */
function KindPreview({ kind, text, name }) {
  const sample = text.trim() || "Nội dung thông báo";
  return (
    <MDBox
      position="relative"
      height={150}
      borderRadius="lg"
      overflow="hidden"
      sx={{
        bgcolor: "#2e3b2f",
        backgroundImage: "linear-gradient(160deg, #4a6b45 0%, #2e3b2f 100%)",
      }}
    >
      {kind === "banner" && (
        <MDBox
          position="absolute"
          top={8}
          left={8}
          right={8}
          px={1}
          py={0.5}
          borderRadius="sm"
          sx={{
            bgcolor: "rgba(0,0,0,0.55)",
            color: "#ffe066",
            fontSize: 12,
            whiteSpace: "nowrap",
            overflow: "hidden",
          }}
        >
          ▶ {sample}
        </MDBox>
      )}
      {(kind === "dialog" || kind === "red") && (
        <MDBox
          position="absolute"
          top="50%"
          left="50%"
          width="70%"
          p={1}
          borderRadius="md"
          textAlign="center"
          sx={{
            transform: "translate(-50%, -50%)",
            bgcolor: "#f5ecd7",
            color: kind === "red" ? "#c62828" : "#3a2a12",
            fontSize: 12,
            boxShadow: "0 0 0 1000px rgba(0,0,0,0.35)",
          }}
        >
          <MDBox sx={{ maxHeight: 54, overflow: "hidden" }}>{sample}</MDBox>
          <MDBox
            mt={0.5}
            mx="auto"
            width={40}
            borderRadius="sm"
            sx={{ bgcolor: "#8a6a2f", color: "#fff", fontSize: 11 }}
          >
            OK
          </MDBox>
        </MDBox>
      )}
      {kind === "popup" && (
        <MDBox
          position="absolute"
          bottom={10}
          left="50%"
          px={1}
          py={0.25}
          borderRadius="sm"
          sx={{
            transform: "translateX(-50%)",
            bgcolor: "rgba(0,0,0,0.6)",
            color: "#fff",
            fontSize: 11,
            maxWidth: "85%",
            whiteSpace: "nowrap",
            overflow: "hidden",
          }}
        >
          {sample}
        </MDBox>
      )}
      {kind === "chat" && (
        <MDBox
          position="absolute"
          bottom={8}
          left={8}
          right={8}
          p={0.75}
          borderRadius="sm"
          sx={{ bgcolor: "rgba(0,0,0,0.45)", color: "#fff", fontSize: 11 }}
        >
          <span style={{ color: "#7fd3ff" }}>[Thế giới] {name || "Admin"}:</span> {sample}
        </MDBox>
      )}
      <MDBox
        position="absolute"
        bottom={4}
        right={6}
        sx={{ color: "rgba(255,255,255,0.5)", fontSize: 10 }}
      >
        minh hoạ màn hình game
      </MDBox>
    </MDBox>
  );
}

KindPreview.propTypes = {
  kind: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
};

function KindCard({ value, selected, onSelect }) {
  const info = KIND_INFO[value];
  return (
    <MDBox
      component="button"
      type="button"
      onClick={() => onSelect(value)}
      aria-pressed={selected}
      p={1.5}
      height="100%"
      width="100%"
      textAlign="left"
      borderRadius="lg"
      sx={({ palette }) => ({
        cursor: "pointer",
        background: selected ? `${palette.info.main}14` : "transparent",
        border: `2px solid ${selected ? palette.info.main : palette.grey[300]}`,
        color: "inherit",
      })}
    >
      <MDBox display="flex" alignItems="center" gap={0.75} mb={0.5}>
        <Icon color={selected ? "info" : "inherit"}>{info.icon}</Icon>
        <MDTypography variant="button" fontWeight="bold">
          {info.label}
        </MDTypography>
        {info.interrupt && (
          <Chip size="small" color="warning" label="che màn hình" sx={{ ml: "auto" }} />
        )}
      </MDBox>
      <MDTypography variant="caption" color="text" display="block" lineHeight={1.4}>
        {info.what}
      </MDTypography>
    </MDBox>
  );
}

KindCard.propTypes = {
  value: PropTypes.string.isRequired,
  selected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
};

function StepTitle({ number, children }) {
  return (
    <MDBox display="flex" alignItems="center" gap={1}>
      <MDBox
        width={22}
        height={22}
        borderRadius="50%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgColor="info"
        color="white"
        sx={{ fontSize: 12, fontWeight: "bold" }}
      >
        {number}
      </MDBox>
      <MDTypography variant="button" fontWeight="bold">
        {children}
      </MDTypography>
    </MDBox>
  );
}

StepTitle.propTypes = { number: PropTypes.number.isRequired, children: PropTypes.node.isRequired };

function Broadcast() {
  const confirm = useConfirm();
  const [run, busy] = useRunner();
  const [kind, setKind] = useState("banner");
  const [text, setText] = useState("");
  const [name, setName] = useState("Admin");
  const [auto, setAuto] = useState({
    text: "",
    intervalMinutes: 10,
    endTime: toLocalInput(new Date(Date.now() + 86400000)),
  });
  const banners = useFetch(() => api.get("/api/admin/broadcast/auto"), []);
  const canSend = useCan(PERMISSIONS.BROADCAST_SEND);
  const maps = useFetch(() => (canSend ? api.get("/api/admin/templates/maps") : null), [canSend]);
  const [mapId, setMapId] = useState("");
  const [zoneId, setZoneId] = useState("");
  const selectedMap = (maps.data || []).find((m) => m.mapId === mapId);
  const kinds = mapId === "" ? SERVER_KINDS : ZONE_KINDS;
  const where =
    mapId === ""
      ? "mọi người đang online"
      : `người đang ở ${selectedMap?.name || "map đã chọn"}${
          zoneId === "" ? " (mọi khu)" : ` khu ${zoneId}`
        }`;

  const send = async () => {
    if (!text.trim()) return;
    const info = KIND_INFO[kind];
    const ok = await confirm({
      title: `Gửi ${info.label.toLowerCase()}`,
      message: `Gửi tới ${where}:\n\n"${text.trim()}"${
        info.interrupt
          ? `\n\nKiểu này hiện hộp thoại giữa màn hình, ${
              mapId === "" ? "MỌI người" : "mọi người ở đó"
            } phải bấm OK mới chơi tiếp.`
          : ""
      }`,
      color: info.interrupt && mapId === "" ? "warning" : "info",
      confirmText: "Gửi",
    });
    if (!ok) return;
    const res =
      mapId === ""
        ? await run(
            () => api.post("/api/admin/broadcast", { kind, text, name }),
            (data) => `Đã gửi tới ${data.online} người đang online`
          )
        : await run(
            () =>
              api.post("/api/admin/broadcast/zone", {
                mapId,
                zoneId: zoneId === "" ? null : zoneId,
                kind,
                text,
              }),
            (data) =>
              data.sent > 0
                ? `Đã gửi tới ${data.sent} người`
                : "Gửi xong, nhưng lúc này không có ai ở đó"
          );
    if (res.ok) setText("");
  };

  const intervalMin = Number(auto.intervalMinutes);
  const endDate = new Date(auto.endTime);
  const autoRuns =
    intervalMin > 0 && endDate > new Date()
      ? Math.floor((endDate.getTime() - Date.now()) / (intervalMin * 60000)) + 1
      : 0;
  const autoBlockers = [
    !auto.text.trim() && "Chưa có nội dung.",
    !(intervalMin >= 1) && "Lặp lại ít nhất mỗi 1 phút.",
    (Number.isNaN(endDate.getTime()) || endDate <= new Date()) &&
      "Thời điểm dừng phải ở tương lai.",
  ].filter(Boolean);

  const addAuto = async () => {
    if (autoBlockers.length) return;
    const ok = await confirm({
      title: "Thêm banner tự động",
      message: `"${auto.text.trim()}"\n\nChạy mỗi ${intervalMin} phút tới ${formatDate(
        endDate
      )} (khoảng ${autoRuns} lần).`,
      confirmText: "Thêm banner",
    });
    if (!ok) return;
    const res = await run(
      () =>
        api.post("/api/admin/broadcast/auto", {
          text: auto.text,
          intervalSeconds: Math.round(intervalMin * 60),
          endTime: auto.endTime,
        }),
      "Đã thêm banner tự động"
    );
    if (res.ok) {
      banners.setData(res.result);
      setAuto({ ...auto, text: "" });
    }
  };

  const removeAuto = async (banner) => {
    const ok = await confirm({
      title: "Dừng banner tự động",
      message: `Dừng ngay banner:\n\n"${banner.text}"`,
      color: "error",
      confirmText: "Dừng banner",
    });
    if (!ok) return;
    const res = await run(
      () => api.post("/api/admin/broadcast/auto/remove", { text: banner.text }),
      "Đã dừng banner"
    );
    if (res.ok) banners.setData(res.result);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="broadcast" />
        <Grid container spacing={3}>
          {canSend && (
            <Grid item xs={12} lg={7}>
              <PageCard
                title="Thông báo ngay"
                subtitle="Chỉ người đang online nhìn thấy, gửi là hiện liền"
              >
                <MDBox
                  component="form"
                  display="flex"
                  flexDirection="column"
                  gap={2}
                  onSubmit={(e) => {
                    e.preventDefault();
                    send();
                  }}
                >
                  <StepTitle number={1}>Gửi tới ai</StepTitle>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={mapId === "" ? 12 : 8}>
                      <SelectField
                        fullWidth
                        label="Gửi tới"
                        SelectProps={{ displayEmpty: true }}
                        InputLabelProps={{ shrink: true }}
                        value={mapId}
                        onChange={(e) => {
                          const value = e.target.value;
                          setMapId(value);
                          setZoneId("");
                          const allowed = value === "" ? SERVER_KINDS : ZONE_KINDS;
                          if (!allowed.includes(kind)) setKind(allowed[0]);
                        }}
                      >
                        <MenuItem value="">Toàn máy chủ (mọi người đang online)</MenuItem>
                        {(maps.data || []).map((m) => (
                          <MenuItem key={m.mapId} value={m.mapId}>
                            Chỉ người ở map: {m.name}
                          </MenuItem>
                        ))}
                      </SelectField>
                    </Grid>
                    {mapId !== "" && (
                      <Grid item xs={12} sm={4}>
                        <SelectField
                          fullWidth
                          label="Khu"
                          SelectProps={{ displayEmpty: true }}
                          InputLabelProps={{ shrink: true }}
                          value={zoneId}
                          onChange={(e) => setZoneId(e.target.value)}
                        >
                          <MenuItem value="">Mọi khu</MenuItem>
                          {(selectedMap?.zones || []).map((z) => (
                            <MenuItem key={z} value={z}>
                              Khu {z}
                            </MenuItem>
                          ))}
                        </SelectField>
                      </Grid>
                    )}
                  </Grid>

                  <StepTitle number={2}>Người chơi thấy thông báo kiểu nào</StepTitle>
                  <Grid container spacing={1}>
                    {kinds.map((k) => (
                      <Grid item xs={12} sm={6} key={k}>
                        <KindCard value={k} selected={kind === k} onSelect={setKind} />
                      </Grid>
                    ))}
                  </Grid>
                  {kind === "chat" && (
                    <MDInput
                      label="Tên người gửi hiện trong chat"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  )}

                  <StepTitle number={3}>Nội dung</StepTitle>
                  <MDBox display="flex" gap={0.5} flexWrap="wrap" alignItems="center">
                    <MDTypography variant="caption" color="text" mr={0.5}>
                      Mẫu có sẵn:
                    </MDTypography>
                    {TEMPLATES.map((t) => (
                      <Chip
                        key={t.label}
                        size="small"
                        variant="outlined"
                        label={t.label}
                        onClick={() => setText(t.text)}
                      />
                    ))}
                  </MDBox>
                  <MDInput
                    label="Nội dung thông báo"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    multiline
                    minRows={3}
                    required
                    helperText={`${text.trim().length} ký tự`}
                  />

                  <MDBox>
                    <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                      Xem trước (minh hoạ):
                    </MDTypography>
                    <KindPreview kind={kind} text={text} name={name} />
                  </MDBox>

                  <MDBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                    <MDButton
                      type="submit"
                      variant="gradient"
                      color="info"
                      disabled={busy || !text.trim()}
                    >
                      <Icon>campaign</Icon>&nbsp;Gửi tới{" "}
                      {mapId === "" ? "toàn máy chủ" : "map đã chọn"}
                    </MDButton>
                    {!text.trim() && (
                      <MDTypography variant="caption" color="text">
                        Nhập nội dung (bước 3) để gửi.
                      </MDTypography>
                    )}
                  </MDBox>
                </MDBox>
              </PageCard>
            </Grid>
          )}
          <Grid item xs={12} lg={canSend ? 5 : 12}>
            <PageCard
              title="Banner tự động"
              subtitle="Chữ chạy tự lặp lại theo chu kỳ tới khi hết hạn"
              color="warning"
            >
              <Alert severity="info" sx={{ mb: 2 }}>
                Khởi động lại hoặc bảo trì máy chủ thì banner tự động sẽ mất, cần thêm lại.
              </Alert>
              {canSend && (
                <MDBox
                  component="form"
                  display="flex"
                  flexDirection="column"
                  gap={1.5}
                  onSubmit={(e) => {
                    e.preventDefault();
                    addAuto();
                  }}
                >
                  <MDInput
                    fullWidth
                    label="Nội dung banner"
                    value={auto.text}
                    onChange={(e) => setAuto({ ...auto, text: e.target.value })}
                  />
                  <MDBox>
                    <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                      Lặp lại mỗi:
                    </MDTypography>
                    <MDBox display="flex" gap={0.5} rowGap={2} flexWrap="wrap" alignItems="center">
                      {INTERVAL_PRESETS.map((m) => (
                        <Chip
                          key={m}
                          size="small"
                          label={`${m} phút`}
                          color={intervalMin === m ? "info" : "default"}
                          variant={intervalMin === m ? "filled" : "outlined"}
                          onClick={() => setAuto({ ...auto, intervalMinutes: m })}
                        />
                      ))}
                      <MDInput
                        size="small"
                        type="number"
                        label="Số phút khác"
                        value={auto.intervalMinutes}
                        inputProps={{ min: 1 }}
                        onChange={(e) => setAuto({ ...auto, intervalMinutes: e.target.value })}
                        sx={{ width: 120 }}
                      />
                    </MDBox>
                  </MDBox>
                  <MDBox>
                    <MDInput
                      fullWidth
                      type="datetime-local"
                      label="Dừng lúc"
                      InputLabelProps={{ shrink: true }}
                      value={auto.endTime}
                      onChange={(e) => setAuto({ ...auto, endTime: e.target.value })}
                    />
                    <MDBox display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                      {endPresets().map((p) => (
                        <Chip
                          key={p.label}
                          size="small"
                          variant="outlined"
                          label={p.label}
                          onClick={() => setAuto({ ...auto, endTime: toLocalInput(p.value) })}
                        />
                      ))}
                    </MDBox>
                  </MDBox>
                  {autoBlockers.length === 0 ? (
                    <MDTypography variant="caption" color="text">
                      Sẽ chạy mỗi {intervalMin} phút tới {formatDate(endDate)} — khoảng {autoRuns}{" "}
                      lần.
                    </MDTypography>
                  ) : (
                    autoBlockers.map((b) => (
                      <MDTypography key={b} variant="caption" color="text" display="block">
                        {b}
                      </MDTypography>
                    ))
                  )}
                  <MDBox>
                    <MDButton
                      type="submit"
                      variant="gradient"
                      color="warning"
                      disabled={busy || autoBlockers.length > 0}
                    >
                      <Icon>add</Icon>&nbsp;Thêm banner tự động
                    </MDButton>
                  </MDBox>
                </MDBox>
              )}
              <MDBox mt={2} mx={-2}>
                <MDTypography variant="button" fontWeight="bold" display="block" px={2} mb={0.5}>
                  Đang chạy
                </MDTypography>
                {banners.error && (
                  <MDBox px={2}>
                    <Alert severity="error">Không tải được: {banners.error}</Alert>
                  </MDBox>
                )}
                <AdminTable
                  loading={banners.loading}
                  rows={banners.data || []}
                  rowKey="text"
                  emptyText="Không có banner tự động nào đang chạy"
                  columns={[
                    { key: "text", label: "Nội dung", wrap: true },
                    {
                      key: "intervalSeconds",
                      label: "Lặp mỗi",
                      render: (b) =>
                        b.intervalSeconds >= 60
                          ? `${Math.round(b.intervalSeconds / 60)} phút`
                          : `${b.intervalSeconds} giây`,
                    },
                    {
                      key: "endTime",
                      label: "Dừng lúc",
                      render: (b) => (
                        <MDBox>
                          {formatDate(b.endTime)}
                          <MDTypography variant="caption" color="text" display="block">
                            {timeLeft(b.endTime)}
                          </MDTypography>
                        </MDBox>
                      ),
                    },
                    canSend && {
                      key: "actions",
                      label: "",
                      align: "right",
                      render: (b) => (
                        <MDButton
                          size="small"
                          variant="outlined"
                          color="error"
                          disabled={busy}
                          onClick={() => removeAuto(b)}
                        >
                          Dừng
                        </MDButton>
                      ),
                    },
                  ].filter(Boolean)}
                />
              </MDBox>
            </PageCard>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Broadcast;
